// Utils/Pdf/Declaration/generateDeclarationPDF.js
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DeclarationPdfTemplate from "./DeclarationPdfTemplate";

// Poll for the rendered node instead of relying on flushSync + refs
const waitForRenderedChild = (container, timeout = 3000) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (container.firstElementChild) {
        resolve(container.firstElementChild);
      } else if (Date.now() - start > timeout) {
        reject(
          new Error("Declaration template failed to render within timeout."),
        );
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  });

export const generateDeclarationPDF = async (declaration, user) => {
  if (!declaration) {
    throw new Error("No declaration data provided");
  }

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "-9999px";
  container.style.zIndex = "-1";
  document.body.appendChild(container);

  const root = createRoot(container);

  try {
    // Normal async render — no flushSync, no manual ref plumbing
    root.render(
      <DeclarationPdfTemplate declaration={declaration} user={user} />,
    );

    const targetNode = await waitForRenderedChild(container);

    // ── Wait for all images (logo, frames, signature, profile pic) to load ──
    const images = Array.from(targetNode.querySelectorAll("img"));
    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth !== 0) return resolve();
            img.onload = resolve;
            img.onerror = resolve;
          }),
      ),
    );

    const canvas = await html2canvas(targetNode, {
      scale: 1.7,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    const yOffset = imgHeight < pdfHeight ? (pdfHeight - imgHeight) / 2 : 0;

    pdf.addImage(
      imgData,
      "JPEG",
      0,
      yOffset,
      imgWidth,
      imgHeight,
      undefined,
      "MEDIUM",
    );
    pdf.save(
      `Declaration_${user?.firstName || "User"}_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
};
